import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';

const GstLiabilityReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<any[]>([]);
  const [totals, setTotals] = useState({ total_collected: 0, total_remitted: 0, total_outstanding: 0, count: 0 });
  const [filters, setFilters] = useState({
    travelDateFrom: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    travelDateTo: new Date().toISOString().split('T')[0],
    status: '',
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.travelDateFrom) params.append('travel_date_from', filters.travelDateFrom);
      if (filters.travelDateTo) params.append('travel_date_to', filters.travelDateTo);
      if (filters.status) params.append('status', filters.status);

      const token = sessionStorage.getItem('jwt_token');
      const res = await fetch(`/api/india-ops/reports/gst-liability?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setRecords(data.data);
        setTotals(data.totals || totals);
      }
    } catch (error) {
      console.error('Error loading GST liability:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">🏛️ GST Liability Report</h1>
        <p className="text-gray-600">GST collected from agents vs remitted to government — for monthly filing</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <p className="text-sm text-gray-500">GST Collected</p>
          <p className="text-2xl font-bold text-blue-600">₹{totals.total_collected.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <p className="text-sm text-gray-500">GST Remitted</p>
          <p className="text-2xl font-bold text-green-600">₹{totals.total_remitted.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <p className="text-sm text-gray-500">Outstanding</p>
          <p className={`text-2xl font-bold ${totals.total_outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>₹{totals.total_outstanding.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <p className="text-sm text-gray-500">GST Invoices</p>
          <p className="text-2xl font-bold text-gray-700">{totals.count}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date From</label>
            <Input type="date" value={filters.travelDateFrom} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, travelDateFrom: e.target.value })} className="text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date To</label>
            <Input type="date" value={filters.travelDateTo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, travelDateTo: e.target.value })} className="text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <Select value={filters.status} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, status: e.target.value })} className="text-sm">
              <option value="">All</option>
              <option value="outstanding">Outstanding Only</option>
              <option value="paid">Fully Paid</option>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={loadData} disabled={loading} className="text-sm">{loading ? 'Loading...' : 'Search'}</Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">GST Invoice</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">GSTIN</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Taxable Value</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">GST Collected</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">GST Remitted</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {records.map((r: any) => (
                <tr key={r.booking_id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 text-sm">
                    <div className="font-mono text-xs">{r.gst_invoice_number}</div>
                    <div className="text-xs text-gray-400">{r.invoice_date ? new Date(r.invoice_date).toLocaleDateString('en-GB') : ''}</div>
                  </td>
                  <td className="px-3 py-3 text-sm font-medium">{r.itinerary_id}</td>
                  <td className="px-3 py-3 text-sm">
                    <div className="truncate max-w-[120px]">{r.buyer_name}</div>
                  </td>
                  <td className="px-3 py-3 text-xs font-mono">{r.buyer_gstin || '—'}</td>
                  <td className="px-3 py-3 text-xs">
                    <span className={`px-1.5 py-0.5 rounded ${r.gst_type === 'IGST' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {r.gst_type === 'IGST' ? 'IGST' : 'CGST+SGST'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-sm text-right">₹{r.taxable_value?.toLocaleString('en-IN') || 0}</td>
                  <td className="px-3 py-3 text-sm text-right text-blue-600">₹{r.gst_collected?.toLocaleString('en-IN') || 0}</td>
                  <td className="px-3 py-3 text-sm text-right text-green-600">₹{r.gst_remitted?.toLocaleString('en-IN') || 0}</td>
                  <td className="px-3 py-3 text-sm text-right">
                    <span className={`font-medium ${r.gst_outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      ₹{r.gst_outstanding?.toLocaleString('en-IN') || 0}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            {records.length > 0 && (
              <tfoot className="bg-gray-50 font-medium">
                <tr>
                  <td colSpan={6} className="px-3 py-3 text-sm text-right">Totals:</td>
                  <td className="px-3 py-3 text-sm text-right text-blue-600">₹{totals.total_collected.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-3 text-sm text-right text-green-600">₹{totals.total_remitted.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-3 text-sm text-right text-red-600">₹{totals.total_outstanding.toLocaleString('en-IN')}</td>
                </tr>
              </tfoot>
            )}
          </table>
          {records.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">No GST invoices found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GstLiabilityReport;
